import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Button,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LiveChat from './LiveChat';

/* ------------------ STYLED COMPONENTS ------------------ */

const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: 'white',
  padding: theme.spacing(1.5, 2),
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

const SearchBar = styled(TextField)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: 14,
  '& .MuiOutlinedInput-root': {
    borderRadius: 24,
    padding: '1px 16px',
    '& fieldset': { border: 'none' },
    '& input': {
      padding: '8px 8px',
      fontSize: '0.9rem',
    },
  },
  maxWidth: 700,
  width: '100%',
  boxShadow: '0 1px 6px rgba(32,33,36,.28)',
}));

const StyledBackButton = styled(Button)(({ theme }) => ({
  padding: '5px 10px',
  borderRadius: '7px',
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  borderColor: theme.palette.warning.main,
  backgroundColor: theme.palette.warning.main,
  textTransform: 'uppercase',
  fontSize: '0.65rem',
  marginBottom: theme.spacing(2),
  '&:hover': {
    backgroundColor: '#e69900',
    borderColor: '#e69900',
  },
}));

/* ------------------ MAIN COMPONENT ------------------ */

const HelpCenter = ({ onNavigate, previousPage }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const faqs = [
    {
      question: 'What is ShipperDock?',
      answer:
        'ShipperDock is a web-based loadboard platform that connects verified shippers, carriers, and brokers, enabling direct communication, real-time load discovery, and transparent freight operations.',
    },
    {
      question: 'Who can use ShipperDock?',
      answer:
        'ShipperDock is designed for freight shippers, carriers, and brokers. Each user role has dedicated features and controlled access to ensure security and clarity.',
    },
    {
      question: 'How do I create and verify my account?',
      answer:
        'Your account is created immediately during signup and full platform access is available right away. While verification is in progress, your profile will not display a verified status badge. Once verification is completed, the verified badge will be applied to your account, indicating a confirmed and trusted company profile.',
    },
    {
      question: 'How do shippers post a load?',
      answer:
        'Shippers can post loads directly from their dashboard by entering pickup and delivery locations, equipment type, weight, and scheduling details. Once posted, the load becomes visible to carriers in real time.',
    },
    {
      question: 'How do carriers find loads?',
      answer:
        'Carriers can browse and search loads by route, location, equipment type, and availability.',
    },
    {
      question: 'Can carriers contact shippers directly?',
      answer:
        'Yes. ShipperDock allows direct communication between carriers and shippers using contact information provided with each load.',
    },
    {
      question: 'Does ShipperDock charge booking or brokerage fees?',
      answer:
        'No. ShipperDock does not charge per-load booking or brokerage fees. Pricing is based on transparent subscription plans without hidden costs.',
    },
    {
      question: 'How are freight payments handled?',
      answer:
        'ShipperDock does not process freight payments. Payments are handled directly between shippers and carriers according to agreed terms, allowing flexibility and faster settlements.',
    },
    {
      question: 'Can brokers use ShipperDock?',
      answer:
        'Yes. Brokers can post and manage loads on behalf of clients while maintaining clear communication and verified company identities.',
    },
    {
      question: 'How does ShipperDock ensure trust and safety?',
      answer:
        'The platform uses document verification, DOT/MC validation, secure authentication, role-based access, and monitored user activity to maintain a trusted environment.',
    },
    {
      question: 'Is my data secure on ShipperDock?',
      answer:
        'Yes. ShipperDock uses secure authentication, encrypted data handling, and industry-standard security practices to protect user information.',
    },
    {
      question: 'What should I do if I forget my password?',
      answer:
        'Go to My Account → Account Information. Click on Change Password. A confirmation email will be sent to you. Once confirmed, your password will be updated successfully. Or you can contact support for help.',
    },
    {
      question: 'How do I contact ShipperDock support?',
      answer:
        'If you need further assistance, you can contact our support team using the Contact Support button below. Our team is available to help with platform-related issues.',
    },
  ];

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ bgcolor: '#f8f9fb', minHeight: '100vh', pb: 8 }}>
  
      <HeroSection>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 1.5 }}>
            Help Center
          </Typography>
          <Typography
            variant="body1"
            sx={{ opacity: 0.95, mb: 2, fontSize: '0.95rem', fontWeight: 'bold' }}
          >
            Find answers and guidance for using ShipperDock
          </Typography>

          <SearchBar
            placeholder="Search FAQs (e.g. verification, payments, loads)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Container>
      </HeroSection>

 
      <Container maxWidth="lg" sx={{ mt: 6 }}>
        {previousPage === 'Community Forum' && onNavigate && (
          <StyledBackButton
            startIcon={<ArrowBackIcon />}
            onClick={() => onNavigate('Community Forum')}
          >
            Back to Community Forum
          </StyledBackButton>
        )}

        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Frequently Asked Questions
        </Typography>

        {filteredFaqs.map((faq, index) => (
          <Accordion
            key={index}
            elevation={0}
            sx={{
              mb: 1.2,
              border: '1px solid #e6e9ef',
              borderRadius: 2,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="600">{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary" lineHeight={1.7}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}

        {filteredFaqs.length === 0 && (
          <Typography color="text.secondary" sx={{ mt: 3 }}>
            No results found for "{searchQuery}"
          </Typography>
        )}

        <Divider sx={{ my: 5 }} />

      
        <Box textAlign="center">
          <Typography variant="h6" fontWeight="bold">
            Still need help?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Our support team is ready to assist you.
          </Typography>

          <LiveChat
            customTrigger={
              <Button variant="contained" size="large" sx={{ px: 5, borderRadius: 2 }}>
                Contact Support
              </Button>
            }
          />
        </Box>
      </Container>
    </Box>
  );
};

export default HelpCenter;
